import { Tooltip } from '@mui/material';
import { useCreateEquipmentConstant, useCreateExerciseCategoryConstant } from '_api';
import { Therapist } from '_api/types';
import { RegimenOptions, TherapyTypeOptions, TherapistSpecToType } from '_constants';
import { PrimaryButton } from 'components/atoms';
import {
  CheckboxField,
  CreateSelectMultipleField,
  FileInputField,
  InputField,
  MultipleInputField,
  SelectField,
} from 'components/molecules/formFields';
import React, { useMemo, useState } from 'react';
import { useEquipment, useExercise, useTargetSide, useUser } from 'utils/hooks';

import { FormBuilder } from './form-builder';
import { ExerciseFormProps } from './types';

const SubmitButton = ({
  label,
  disabled,
  tooltip,
}: {
  label: string;
  disabled: boolean;
  tooltip: string | undefined;
}) => (
  <Tooltip title={tooltip} placement="top">
    <span>
      <PrimaryButton disabled={disabled || false} style={{ width: '100%' }} type="submit">
        {label}
      </PrimaryButton>
    </span>
  </Tooltip>
);

export const ExerciseForm = ({
  disableAll = false,
  disableSubmit = false,
  hideSubmitButtons = false,
  initValue,
  onCopy,
  onBack,
  onSubmit,
  onUpdate,
  submitLabel = 'Submit',
  disabledSubmitTooltip,
}: ExerciseFormProps) => {
  const { mutateAsync: createCategory } = useCreateExerciseCategoryConstant();
  const { mutateAsync: createEquipment } = useCreateEquipmentConstant();
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const { equipmentOptions } = useEquipment();
  const { exerciseCategoryOptions } = useExercise();
  const { targetSideOptions } = useTargetSide();
  const { isTherapist, account } = useUser();

  const therapistType = useMemo(() => {
    if (isTherapist && account) {
      const therapist = account as Therapist;
      return TherapistSpecToType(therapist.therapistSpecializations[0].name);
    }
    return undefined;
  }, [account, isTherapist]);

  const update = async (key: any, arg: any) => {
    if (onUpdate) onUpdate(key, arg);
  };

  const onAddCategoryOption = async (option: string) => {
    return await createCategory({ title: option });
  };

  const onAddEquipmentOption = async (option: string) => {
    return await createEquipment({ title: option });
  };

  const formFields = [
    {
      component: InputField,
      fieldKey: 'title',
      label: 'EXERCISE TITLE',
      required: true,
      xs: 12,
    },
    {
      component: SelectField,
      fieldKey: 'targetSide',
      label: 'TARGET SIDE',
      options: targetSideOptions || [],
      required: true,
      xs: 4,
    },
    {
      component: CreateSelectMultipleField,
      fieldKey: 'equipment',
      label: 'EQUIPMENT NEEDED',
      options: equipmentOptions || [],
      onAddOption: onAddEquipmentOption,
      required: true,
      xs: 4,
    },
    {
      component: CreateSelectMultipleField,
      fieldKey: 'category',
      label: 'CATEGORY',
      options: exerciseCategoryOptions || [],
      onAddOption: onAddCategoryOption,
      required: true,
      xs: 4,
    },
    {
      component: SelectField,
      fieldKey: 'defaultExerciseDetail',
      label: 'EXERCISE DETAIL',
      options: RegimenOptions,
      xs: 6,
    },
    {
      component: SelectField,
      fieldKey: 'defaultTherapyType',
      label: 'THERAPY TYPE',
      options: TherapyTypeOptions,
      defaultValue: initValue?.id == null ? therapistType : undefined,
      xs: 6,
    },
    {
      component: InputField,
      fieldKey: 'defaultSets',
      label: 'SETS',
      xs: 3,
    },
    {
      component: InputField,
      fieldKey: 'defaultReps',
      label: 'REPETITIONS',
      xs: 3,
    },
    {
      component: InputField,
      fieldKey: 'defaultPerDay',
      label: 'TIMES PER DAY',
      xs: 3,
    },
    {
      component: InputField,
      fieldKey: 'defaultPerWeek',
      label: 'TIMES PER WEEK',
      xs: 3,
    },
    {
      component: InputField,
      fieldKey: 'notes',
      label: 'NOTES',
      xs: 12,
    },
    {
      component: InputField,
      fieldKey: 'description',
      label: 'DESCRIPTION',
      type: 'textarea',
      multiline: true,
      rows: 4,
    },
    {
      component: MultipleInputField,
      fieldKey: 'instructionList',
      label: 'INSTRUCTION LIST',
      xs: 12,
    },
    {
      component: FileInputField,
      fieldKey: 'imageUrl',
      label: 'THUMBNAIL IMAGE',
      isUploadingFile: (uploading: boolean) => setIsUploadingFile(uploading),
      xs: 6,
    },
    {
      component: FileInputField,
      fieldKey: 'videoLink',
      label: 'VIDEO LINK',
      acceptFileType: 'video/mp4,video/webm,video/ogg',
      isUploadingFile: (uploading: boolean) => setIsUploadingFile(uploading),
      xs: 6,
    },
    {
      component: CheckboxField,
      fieldKey: 'shareWithOrg',
      label: 'SHARE',
      checkboxLabel: 'Share with Your Organization',
      xs: 4,
    },
  ];

  return (
    <FormBuilder
      disabled={disableAll}
      disableSubmit={isUploadingFile || disableSubmit}
      disableTertiary={isUploadingFile}
      initValues={initValue}
      formFields={formFields}
      onBack={onBack}
      onSubmit={onSubmit}
      onTertiary={onCopy}
      onUpdate={update}
      tertiaryLabel="Create As New Copy"
      hideSubmit={hideSubmitButtons}
      rowSpacing={10}
      submitButton={() =>
        SubmitButton({
          label: submitLabel,
          disabled: isUploadingFile || disableSubmit,
          tooltip: disableSubmit ? disabledSubmitTooltip : undefined,
        })
      }
    />
  );
};
